import { useEffect, useRef, useState } from "react";
import { Mic, Square, Trash2, ImagePlus, LayoutGrid, MessagesSquare } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, formatBlobError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TemplateGallery } from "@/components/TemplateGallery";
import { BriefBuilder } from "@/components/BriefBuilder";
import { DomainManager } from "@/components/DomainManager";
import MLModelPanel from "@/components/MLModelPanel";
import UMLPanel from "@/components/UMLPanel";
import { TEMPLATES } from "@/data/templates";

const inputCls =
  "w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-600 focus:outline-none focus:border-cyan-600 focus:ring-2 focus:ring-cyan-500/20 text-xs transition-all";
const smallBtnCls =
  "text-xs font-semibold px-3 py-2 rounded border transition-colors code-font";

export default function Workbench({ onOpenTeam } = {}) {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState(null);
  const [code, setCode] = useState("");
  const [previewCode, setPreviewCode] = useState("");
  const [prompt, setPrompt] = useState("");
  const [genModel, setGenModel] = useState("gpt-5-nano");
  const [genType, setGenType] = useState("site"); // site | video
  const [videoStatus, setVideoStatus] = useState("idle"); // idle | running | done | error
  const [videoError, setVideoError] = useState("");
  const [videoEngine, setVideoEngine] = useState(null); // "credits" | "veo" | "none" | null(unknown)
  const videoTimerRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [newClient, setNewClient] = useState("");
  const [status, setStatus] = useState("In Development");
  const [stage, setStage] = useState("");
  const [notes, setNotes] = useState("");
  const [description, setDescription] = useState("");
  const [deployUrl, setDeployUrl] = useState("");
  const [shareToken, setShareToken] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [build, setBuild] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [attachingImages, setAttachingImages] = useState(false);
  const [autoPreview, setAutoPreview] = useState(true);
  const [saveState, setSaveState] = useState("saved"); // saved | unsaved | saving | error
  const savedCodeRef = useRef("");
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showBriefBuilder, setShowBriefBuilder] = useState(false);
  const [genChoiceOpen, setGenChoiceOpen] = useState(false);
  const [versions, setVersions] = useState(null);
  const [briefLang, setBriefLang] = useState("en");
  const [newTemplate, setNewTemplate] = useState("");
  const [newType, setNewType] = useState("website");
  const editorRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const imageFiles = files.filter((f) => (f.content_type || "").startsWith("image/"));

  // Poll an in-progress APK/AAB build until it finishes.
  const [toolchain, setToolchain] = useState(null);
  const [uploadingApk, setUploadingApk] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/platform/toolchain-status").then((r) => setToolchain(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    // Auto live-preview: refresh the output shortly after the code stops changing.
    if (!autoPreview) return;
    const t = setTimeout(() => setPreviewCode(code), 700);
    return () => clearTimeout(t);
  }, [code, autoPreview]);

  useEffect(() => () => clearInterval(videoTimerRef.current), []);

  useEffect(() => {
    // Live save: any code change is persisted automatically a few seconds after
    // typing stops — the public page updates immediately and the next APK download
    // carries the new design. Free, no AI. Manual Save Build still works.
    if (!active || generating) return;
    if (code === savedCodeRef.current) return;
    setSaveState("unsaved");
    const t = setTimeout(async () => {
      setSaveState("saving");
      try {
        const { data } = await api.put(`/projects/${active.id}?lite=1`, { html: code });
        savedCodeRef.current = code;
        setActive(data);
        setSaveState("saved");
      } catch {
        setSaveState("error");
      }
    }, 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, active?.id, generating]);

  useEffect(() => {
    if (!build || build.status !== "building") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/builds/${build.id}`);
        const merged = { ...data, id: build.id, build_type: build.build_type };
        setBuild(merged);
        if (data.status !== "building") {
          clearInterval(t);
          if (data.status === "done") toast.success(`${(build.build_type || "").toUpperCase()} is ready — download it below`);
          else toast.error("Build failed — expand the log below");
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => clearInterval(t);
  }, [build]);

  const STATUSES = ["In Development", "In Review", "Staging Preview", "Live", "On Hold"];

  const loadProjects = () =>
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});

  useEffect(() => {
    loadProjects();
  }, []);

  const openProject = async (p) => {
    try {
      const { data } = await api.get(`/projects/${p.id}`);
      setActive(data);
      setCode(data.html || "");
      setPreviewCode(data.html || "");
      savedCodeRef.current = data.html || "";
      setSaveState("saved");
      setStatus(data.status || "In Development");
      setStage(data.stage || "");
      setNotes(data.notes || "");
      setDescription(data.description || "");
      setDeployUrl(data.deploy_url || "");
      setShareToken(data.share_token || "");
      loadFiles(p.id);
      setPrompt(""); // fresh prompt box per project — no stale text from another project
      setShowImagePicker(false);
    } catch {
      toast.error("Could not load project");
    }
  };

  const applyBrief = (brief) => {
    setPrompt(brief);
    setShowTemplates(false);
    setShowBriefBuilder(false);
    toast.success(
      briefLang === "te"
        ? "వివరణ సిద్ధం — సైట్ తయారు చేయడానికి Generate Draft నొక్కండి (క్రెడిట్లు వాడబడతాయి)"
        : "Brief filled below — press Generate Draft when ready (credits used only then)"
    );
  };

  const createProject = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/projects", { name: newName, client_email: newClient, project_type: newType });
      toast.success("Project created");
      setNewName("");
      setNewClient("");
      await loadProjects();
      await openProject(data);
      if (newType === "cyber") {
        // Cybersecurity service projects auto-load the service-app brief (booking + tools)
        const tpl = TEMPLATES.find((t) => t.id === "cybersecurity_app");
        if (tpl) setPrompt(tpl.brief);
        toast.success("Cybersecurity service brief loaded — an admin must grant access before generating");
        setNewTemplate("");
      } else if (newTemplate) {
        const tpl = TEMPLATES.find((t) => t.id === newTemplate);
        if (tpl) {
          setPrompt(tpl.brief);
          toast.success(`"${tpl.en}" template loaded — press Generate Draft when ready`);
        }
        setNewTemplate("");
      }
      setNewType("website");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const requestCyberAccess = async (p) => {
    try {
      await api.post(`/projects/${p.id}/cyber-access/request`);
      toast.success("Access request sent to admin");
      await loadProjects();
      if (active?.id === p.id) await openProject({ ...p });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const decideCyberAccess = async (p, decision) => {
    try {
      await api.post(`/projects/${p.id}/cyber-access/decide`, { decision });
      toast.success(decision === "granted" ? "Cybersecurity access granted" : "Access revoked");
      await loadProjects();
      if (active?.id === p.id) await openProject({ ...p });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const loadFiles = (projectId) =>
    api.get(`/projects/${projectId}/files`).then((r) => setFiles(r.data)).catch(() => setFiles([]));

  const uploadProjectFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !active) return;
    if (f.size > 100 * 1024 * 1024) return toast.error("File too large (max 100 MB)");
    const fd = new FormData();
    fd.append("file", f);
    setUploadingFile(true);
    try {
      await api.post(`/projects/${active.id}/files`, fd);
      toast.success(`"${f.name}" uploaded to cloud storage`);
      loadFiles(active.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingFile(false);
    }
  };

  // Attach images straight from the prompt box — uploads them to the project and
  // notes them in the brief so the next Generate/Improve places them in the design.
  const attachImages = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length || !active) return;
    const images = list.filter((f) => (f.type || "").startsWith("image/"));
    if (!images.length) return toast.error("Choose image files (jpg, png, webp…)");
    setAttachingImages(true);
    const names = [];
    for (const f of images) {
      if (f.size > 100 * 1024 * 1024) {
        toast.error(`"${f.name}" too large (max 100 MB)`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/projects/${active.id}/files`, fd);
        names.push(f.name);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || `"${f.name}" rejected`);
      }
    }
    setAttachingImages(false);
    loadFiles(active.id);
    if (names.length) {
      toast.success(`${names.length} image(s) attached — Generate/Improve will use them in the design`);
      const note = `Use my attached images: ${names.join(", ")} (place them where they fit — logo, hero, gallery)`;
      setPrompt((p) => (p.includes(note) ? p : p.trim() ? `${p.trim()}\n${note}` : note));
    }
  };

  const downloadProjectFile = async (file) => {
    try {
      const res = await api.get(`/files/${file.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(await formatBlobError(err));
    }
  };

  const copyFileLink = (file) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${file.id}/public`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Public link copied — paste it as an <img src> or link inside the site code"),
      () => toast.error(url)
    );
  };

  const deleteProjectFile = async (file) => {
    if (!window.confirm(`Delete "${file.filename}"? Links inside generated sites will stop working.`)) return;
    try {
      await api.delete(`/files/${file.id}`);
      toast.success("File deleted");
      loadFiles(active.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const insertImage = (file) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${file.id}/public`;
    const alt = file.filename.replace(/"/g, "");
    const tag = `<img src="${url}" alt="${alt}" style="max-width:100%;height:auto">`;
    const ta = editorRef.current;
    const pos = ta ? ta.selectionStart ?? code.length : code.length;
    setCode(code.slice(0, pos) + "\n" + tag + "\n" + code.slice(pos));
    setShowImagePicker(false);
    toast.success("Image tag inserted at the cursor — press Run Preview to see it");
    requestAnimationFrame(() => {
      if (ta) {
        ta.focus();
        const end = pos + tag.length + 2;
        ta.setSelectionRange(end, end);
      }
    });
  };

  const downloadHandoffPack = async () => {
    if (!active) return;
    try {
      const res = await api.get(`/projects/${active.id}/handoff-pack`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${active.name}-handoff.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Handoff pack downloaded (frontend + backend + design PDF + APK)");
    } catch (err) {
      toast.error(await formatBlobError(err));
    }
  };

  const toggleRecording = async () => {
    if (recording) {
      mediaRecorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks = [];
      const rec = new MediaRecorder(stream);
      mediaRecorderRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1000) {
          setRecording(false);
          return;
        }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const res = await api.post("/voice/transcribe", fd);
          const text = (res.data?.text || "").trim();
          if (text) {
            setPrompt((p) => (p ? p + " " + text : text));
            toast.success("Voice typed into the prompt box");
          } else {
            toast.info("Nothing heard — try again, speak a little longer");
          }
        } catch (err) {
          toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Voice transcription failed");
        } finally {
          setTranscribing(false);
          setRecording(false);
        }
      };
      rec.start();
      setRecording(true);
      toast.info("Recording… speak in Telugu or English. Tap the mic again to stop.");
    } catch {
      toast.error("Microphone not available — allow mic permission in the browser");
    }
  };

  const deleteProject = async (p) => {
    if (!window.confirm(`Delete project "${p.name}" permanently? Its uploaded files and app builds are removed too. This cannot be undone.`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success(`Project "${p.name}" deleted`);
      if (active?.id === p.id) {
        setActive(null);
        setCode("");
        setPreviewCode("");
        setFiles([]);
      }
      loadProjects();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const uploadAppBuild = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !active) return;
    if (!/\.(apk|aab)$/i.test(f.name)) return toast.error("Choose a .apk or .aab file");
    const fd = new FormData();
    fd.append("file", f);
    setUploadingApk(true);
    try {
      const res = await api.post(`/projects/${active.id}/app-build/upload`, fd);
      toast.success(`${res.data.build_type.toUpperCase()} attached to cloud storage — client download link is live`);
      setBuild({ id: res.data.id, status: "done", build_type: res.data.build_type, uploaded: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingApk(false);
    }
  };

  // Video mode: check which engine is available (credits by default, Veo-3 when
  // the owner's Google key is saved) so we can label it honestly up front.
  useEffect(() => {
    if (genType !== "video") return;
    let cancelled = false;
    api.get("/settings/provider-keys/status")
      .then(({ data }) => { if (!cancelled) setVideoEngine(data.video_engine || null); })
      .catch(() => { if (!cancelled) setVideoEngine(null); });
    return () => { cancelled = true; };
  }, [genType]);

  const generateVideo = async () => {
    if (!prompt.trim()) {
      toast.error("Type what the video should show in the brief box first");
      return;
    }
    setVideoStatus("running");
    setVideoError("");
    try {
      const { data } = await api.post(`/projects/${active.id}/video`, { prompt: prompt.trim() });
      videoTimerRef.current = setInterval(async () => {
        try {
          const st = (await api.get(`/projects/${active.id}/video-status/${data.job_id}`)).data;
          if (st.status === "done") {
            clearInterval(videoTimerRef.current);
            setVideoStatus("done");
            toast.success("Video ready — saved in Project Files (Files tab)");
            loadFiles(active.id);
          } else if (st.status === "error") {
            clearInterval(videoTimerRef.current);
            setVideoStatus("error");
            setVideoError(st.error || "Video generation failed");
          }
        } catch {
          /* transient poll error — keep polling */
        }
      }, 5000);
    } catch (err) {
      setVideoStatus("error");
      setVideoError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const loadVersions = async () => {
    if (!active) return;
    try {
      const { data } = await api.get(`/projects/${active.id}/versions`);
      setVersions(data.versions || []);
    } catch {
      setVersions([]);
    }
  };

  const restoreVersion = async (index) => {
    try {
      const { data } = await api.post(`/projects/${active.id}/versions/${index}/restore`);
      setActive(data);
      setCode(data.html || "");
      setPreviewCode(data.html || "");
      savedCodeRef.current = data.html || "";
      setSaveState("saved");
      setVersions(null);
      toast.success("Previous design restored — live site and next APK updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };


  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${active.id}`, { html: code, status, stage, notes, description, deploy_url: deployUrl });
      setActive(data);
      setPreviewCode(code);
      savedCodeRef.current = code;
      setSaveState("saved");
      toast.success("Build saved — preview link and installable APK both updated");
      loadProjects();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!active) return;
    setPublishing(true);
    try {
      const { data } = await api.post(`/projects/${active.id}/publish`);
      setShareToken(data.share_token);
      toast.success("Published — live preview link is ready");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPublishing(false);
    }
  };

  const publishUrl = shareToken ? `${process.env.REACT_APP_BACKEND_URL}/api/p/${shareToken}` : "";

  const startBuild = async (type) => {
    if (!active) return;
    try {
      const { data } = await api.post(`/projects/${active.id}/app-build`, { build_type: type });
      setBuild({ ...data, build_type: type });
      toast.success(data.status === "done"
        ? `${type.toUpperCase()} ready instantly — press Download`
        : `${type.toUpperCase()} build started — usually ready in a few minutes`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadBuild = async () => {
    if (!build?.id) return;
    try {
      const res = await api.get(`/builds/${build.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${active.name}.${build.build_type}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${(build.build_type || "").toUpperCase()} downloaded`);
    } catch (err) {
      toast.error(await formatBlobError(err));
    }
  };

  const capacitorZip = async () => {
    if (!active) return;
    try {
      const res = await api.get(`/projects/${active.id}/capacitor-zip`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${active.name}-capacitor.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Capacitor project downloaded — build commands inside README");
    } catch (err) {
      toast.error(await formatBlobError(err));
    }
  };

  const requestDeploy = async () => {
    if (!active) return;
    try {
      await api.post(`/projects/${active.id}/deploy-request`);
      toast.success("Production deploy requested — admin sees it in the Command Deck");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publishUrl);
      toast.success("Link copied");
    } catch {
      toast.info(publishUrl);
    }
  };

  const downloadCode = async () => {
    if (!active) return;
    if (!code) return toast.info("No code saved yet — generate or write some first");
    try {
      const res = await api.get(`/projects/${active.id}/frontend-zip`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${active.name}-frontend.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Front-end project downloaded (zip: index.html + assets/css + assets/js)");
    } catch (err) {
      toast.error(await formatBlobError(err));
    }
  };

  const downloadBackendCode = async () => {
    try {
      const res = await api.get("/platform/backend-zip", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "workdesk-backend.zip";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Back-end project downloaded (zip: main.py + src/ + requirements + schema)");
    } catch (err) {
      toast.error(await formatBlobError(err));
    }
  };

  const downloadDatabaseSchema = async () => {
    try {
      const res = await api.get("/platform/database-schema", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "workdesk-database-schema.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Database design downloaded (JSON)");
    } catch (err) {
      toast.error(await formatBlobError(err));
    }
  };

  const downloadPdf = async () => {
    if (!active) return;
    try {
      const res = await api.get(`/projects/${active.id}/document`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${active.name}-design.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Design PDF downloaded");
    } catch (err) {
      toast.error(await formatBlobError(err));
    }
  };

  const generate = async (mode = "fresh", skipImproveConfirm = false) => {
    if (!active || !prompt.trim() || generating) return;
    if (mode === "improve" && !code) return toast.info("No draft yet — generate one first, then improve it");
    const pricey = {
      "gpt-5.4": "DSS AI Ultra",
      "claude-sonnet-5-5": "DSS AI Premium",
      "claude-sonnet-4-6": "DSS AI Sonnet",
      "claude-opus-4-8": "DSS AI Opus",
    }[genModel];
    // Premium heads-up: friendly, and asked only ONCE per model per session —
    // repeated popups looked like errors to the user.
    const seenKey = `premium-ok-${genModel}`;
    if (pricey && !sessionStorage.getItem(seenKey)) {
      if (!window.confirm(
        `Heads up: ${pricey} is a premium model and uses more credits per draft. ` +
        `Cheaper choices are in the dropdown (DSS AI Nano, DSS AI Fast).\n\nPress OK to continue with ${pricey}.`
      )) return;
      sessionStorage.setItem(seenKey, "1");
    }
    if (mode === "improve" && !skipImproveConfirm && !window.confirm(
      "Improve the CURRENT draft with your instruction? The AI reads the full current code and applies your change (uses credits like a mini-generation). Continue?"
    )) return;
    setGenerating(true);
    if (mode === "fresh") setCode("");

    try {
      // Job-based generation: the server keeps working even if the connection
      // drops or the tab closes (multi-minute 3D/premium drafts survive).
      const { data: job } = await api.post(`/projects/${active.id}/generate`, { prompt, model: genModel, mode });
      const startedAt = Date.now();
      let polls = 0;
      let lastText = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 2000));
        const st = (await api.get(`/projects/${active.id}/generate-status/${job.job_id}`)).data;
        if (st.text && st.text !== lastText) {
          lastText = st.text;
          setCode(st.text);          // editor fills live as the AI writes
          setPreviewCode(st.text);   // preview renders live too
        }
        if (st.status === "done") {
          const finalHtml = st.html || st.text;
          setCode(finalHtml);
          setPreviewCode(finalHtml);
          savedCodeRef.current = finalHtml; // generate already saved it server-side
          setSaveState("saved");
          toast.success(mode === "improve" ? "Draft updated with your change" : "Draft generated and saved — edit freely, then Run Preview");
          loadProjects();
          break;
        }
        if (st.status === "error") throw new Error(st.error || "Generation failed");
        polls += 1;
        if (Date.now() - startedAt > 15 * 60 * 1000) throw new Error("Generation is taking too long — press Generate again");
        if (polls > 450) throw new Error("Generation is taking too long — press Generate again");
      }
    } catch (err) {
      // 401s are already handled by the api interceptor (silent refresh, else logout prompt)
      if (err.response?.status !== 401) {
        const detail = err.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : err.message || "Generation failed");
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="workbench">
      {/* Sidebar: new project + project list */}
      <div className="lg:col-span-3 space-y-4">
        <form onSubmit={createProject} className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-3" data-testid="new-project-form">
          <div className="text-xs uppercase code-font text-cyan-700 dark:text-cyan-300 font-bold">New Project</div>
          <input
            className={inputCls}
            placeholder="Project name"
            required
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            data-testid="new-project-name-input"
          />
          <input
            className={inputCls}
            placeholder="Client email"
            type="email"
            required
            value={newClient}
            onChange={(e) => setNewClient(e.target.value)}
            data-testid="new-project-client-input"
          />
          <select
            className={inputCls}
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
            data-testid="new-project-type-select"
            title="Project type — cybersecurity service projects need one-tap admin approval before generating"
          >
            <option value="website">Website</option>
            <option value="app">Web App</option>
            <option value="cyber">Cybersecurity Service (admin approval needed)</option>
          </select>
          <select
            className={inputCls}
            value={newTemplate}
            onChange={(e) => setNewTemplate(e.target.value)}
            data-testid="new-project-template-select"
            title="Optional: start from a ready template brief — free, credits used only when you press Generate"
          >
            <option value="">No template — blank start</option>
            {TEMPLATES.map((t) => (
              <option key={t.id} value={t.id}>{t.en} / {t.te}</option>
            ))}
          </select>
          <button
            type="submit"
            className="w-full px-3 py-2 rounded bg-cyan-700 text-white font-bold uppercase text-xs tracking-wider hover:bg-cyan-800 transition-colors"
            data-testid="create-project-button"
          >
            Create Project
          </button>
        </form>

        <div className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
          <div className="text-xs uppercase code-font text-slate-500 dark:text-slate-400 mb-3">Client Projects</div>
          <div className="space-y-2" data-testid="project-list">
            {projects.length === 0 && (
              <p className="text-xs text-slate-500 dark:text-slate-400 code-font" data-testid="project-list-empty">No projects yet.</p>
            )}
            {projects.map((p) => (
              <div key={p.id} className="flex items-stretch gap-1.5">
                <button
                  onClick={() => openProject(p)}
                  className={`flex-1 min-w-0 text-left p-3 rounded-lg border transition-all ${
                    active?.id === p.id
                      ? "bg-cyan-50 dark:bg-cyan-400/10 border-cyan-300 dark:border-cyan-400/40 dark:shadow-[0_0_18px_-6px_rgba(0,240,255,0.5)]"
                      : "bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 hover:border-cyan-400/50 dark:hover:border-cyan-400/30"
                  }`}
                  data-testid={`project-item-${p.id}`}
                >
                  <div className="text-sm text-slate-900 dark:text-white font-semibold truncate">
                    {p.name}
                    {p.project_type === "cyber" && (
                      <span
                        className={`ml-1.5 align-middle text-[9px] code-font px-1.5 py-0.5 rounded-full border ${
                          p.cyber_access === "granted"
                            ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/40"
                            : p.cyber_access === "requested"
                              ? "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700/40"
                              : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700"
                        }`}
                        data-testid={`project-cyber-badge-${p.id}`}
                      >
                        CYBER {p.cyber_access === "granted" ? "✓" : p.cyber_access === "requested" ? "…" : "LOCK"}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 code-font truncate">{p.client_email}</div>
                </button>
                {user?.role === "admin" && p.project_type === "cyber" && p.cyber_access === "requested" && (
                  <button
                    onClick={() => decideCyberAccess(p, "granted")}
                    className="px-2.5 rounded-lg border bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/40 hover:bg-emerald-100 transition-colors text-[10px] code-font font-bold"
                    data-testid={`project-cyber-grant-${p.id}`}
                    title={`Grant cybersecurity access for "${p.name}"`}
                  >
                    GRANT
                  </button>
                )}
                {user?.role === "admin" && p.project_type === "cyber" && p.cyber_access === "granted" && (
                  <button
                    onClick={() => decideCyberAccess(p, "revoked")}
                    className="px-2.5 rounded-lg border bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100 transition-colors text-[10px] code-font font-bold"
                    data-testid={`project-cyber-revoke-${p.id}`}
                    title={`Revoke cybersecurity access for "${p.name}"`}
                  >
                    REVOKE
                  </button>
                )}
                {user?.role === "admin" && (
                  <button
                    onClick={() => deleteProject(p)}
                    className="px-2.5 rounded-lg border bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-700/40 hover:bg-rose-100 dark:hover:bg-rose-500/20 transition-colors"
                    data-testid={`project-delete-${p.id}`}
                    title={`Delete project "${p.name}" (admin only)`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main: prompt + editor + preview */}
      <div className="lg:col-span-9 space-y-4">
        {!active ? (
          <div className="p-12 rounded-xl bg-white dark:bg-slate-900 border border-dashed border-slate-200 dark:border-slate-800 text-center" data-testid="workbench-empty">
            <div className="text-xs code-font text-cyan-700 dark:text-cyan-300 uppercase">Workbench Ready</div>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Select a client project or create a new one to start building.</p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl glass-panel shadow-lg" data-testid="active-project-header">
              <div>
                <div className="text-sm font-bold text-slate-900 dark:text-white" data-testid="active-project-name">{active.name}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 code-font">{active.client_email}</div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPreviewCode(code)}
                  className={`${smallBtnCls} bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-700 hover:bg-slate-200`}
                  data-testid="run-preview-button"
                >
                  Run Preview
                </button>
                <button
                  onClick={downloadPdf}
                  className={`${smallBtnCls} bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/40 hover:bg-emerald-100 dark:hover:bg-emerald-500/20`}
                  data-testid="download-design-pdf-button"
                >
                  Design PDF
                </button>
                <button
                  onClick={downloadCode}
                  className={`${smallBtnCls} bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-700/40 hover:bg-cyan-100 dark:hover:bg-cyan-500/20`}
                  data-testid="download-frontend-code-button"
                >
                  Frontend Code
                </button>
                <button
                  onClick={downloadBackendCode}
                  className={`${smallBtnCls} bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-700/40 hover:bg-amber-100 dark:hover:bg-amber-500/20`}
                  data-testid="download-backend-code-button"
                >
                  Backend Code
                </button>
                <button
                  onClick={downloadDatabaseSchema}
                  className={`${smallBtnCls} bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-700/40 hover:bg-violet-100 dark:hover:bg-violet-500/20`}
                  data-testid="download-database-schema-button"
                >
                  Database
                </button>
                <button
                  onClick={downloadHandoffPack}
                  className={`${smallBtnCls} bg-pink-50 dark:bg-pink-500/10 text-pink-700 dark:text-pink-300 border-pink-200 dark:border-pink-700/40 hover:bg-pink-100 dark:hover:bg-pink-500/20`}
                  data-testid="download-handoff-pack-button"
                  title="One ZIP with everything for the client: frontend + backend + design PDF + APK"
                >
                  Handoff Pack
                </button>
                <button
                  onClick={publish}
                  disabled={publishing}
                  className={`${smallBtnCls} bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700/40 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 disabled:opacity-60`}
                  data-testid="publish-button"
                >
                  {publishing ? "Publishing…" : shareToken ? "Re-Publish" : "Publish"}
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="px-4 py-2 rounded bg-cyan-700 text-white font-bold uppercase text-xs tracking-wider hover:bg-cyan-800 transition-colors disabled:opacity-60"
                  data-testid="save-project-button"
                >
                  {saving ? "Saving…" : "Save Build"}
                </button>
                <span className="relative">
                  <button
                    onClick={() => (versions === null ? loadVersions() : setVersions(null))}
                    className={`${smallBtnCls} bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-cyan-400`}
                    data-testid="versions-button"
                    title="Every design change is backed up automatically — restore any previous design"
                  >
                    History
                  </button>
                  {versions !== null && (
                    <div className="absolute right-0 top-9 z-30 w-64 rounded-lg bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 shadow-xl p-2" data-testid="versions-panel">
                      <div className="text-[10px] uppercase font-bold text-slate-400 code-font px-2 py-1">Previous designs (auto-saved)</div>
                      {versions.length === 0 ? (
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 px-2 py-1" data-testid="versions-empty">No previous versions yet — they appear after the first change.</p>
                      ) : (
                        <ul className="max-h-56 overflow-y-auto space-y-1">
                          {[...versions].reverse().map((v) => (
                            <li key={v.index}>
                              <button
                                type="button"
                                onClick={() => window.confirm("Restore this older design? Your current design is also kept in history.") && restoreVersion(v.index)}
                                className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-[11px] text-slate-700 dark:text-slate-200 transition-colors"
                                data-testid={`version-restore-${v.index}`}
                              >
                                {new Date(v.at).toLocaleString()} · {Math.round(v.chars / 1024)}k chars
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </span>
              </div>
            </div>

            {shareToken && (
              <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-700/40" data-testid="publish-link-bar">
                <span className="text-xs code-font text-indigo-700 dark:text-indigo-300 uppercase font-bold">Live Preview</span>
                <a
                  href={publishUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs code-font text-cyan-800 dark:text-cyan-300 hover:text-cyan-800 break-all underline"
                  data-testid="publish-link"
                >
                  {publishUrl}
                </a>
                <button
                  onClick={copyLink}
                  className="text-xs px-2.5 py-1 rounded border bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-700 hover:bg-slate-200 code-font transition-colors"
                  data-testid="copy-publish-link-button"
                >
                  Copy
                </button>
              </div>
            )}

            <div className="premium-card grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/[0.08]" data-testid="project-status-panel">
              <div>
                <label className="block text-xs uppercase font-semibold text-slate-500 dark:text-slate-400 code-font mb-2" htmlFor="project-status">Status (client-visible)</label>
                <select id="project-status" className={inputCls} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="project-status-select">
                  {STATUSES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase font-semibold text-slate-500 dark:text-slate-400 code-font mb-2" htmlFor="project-stage">Current Stage</label>
                <input id="project-stage" className={inputCls} placeholder="e.g. Homepage draft" value={stage} onChange={(e) => setStage(e.target.value)} data-testid="project-stage-input" />
              </div>
              <div>
                <label className="block text-xs uppercase font-semibold text-slate-500 dark:text-slate-400 code-font mb-2" htmlFor="project-notes">Client Note</label>
                <input id="project-notes" className={inputCls} placeholder="Progress note the client will read" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="project-notes-input" />
              </div>
            </div>

            <div className="premium-card grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/[0.08]" data-testid="project-details-panel">
              <div>
                <label className="block text-xs uppercase font-semibold text-slate-500 dark:text-slate-400 code-font mb-2" htmlFor="project-description">Project Explanation (goes in PDF)</label>
                <input id="project-description" className={inputCls} placeholder="What this project is and does" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="project-description-input" />
              </div>
              <div>
                <label className="block text-xs uppercase font-semibold text-slate-500 dark:text-slate-400 code-font mb-2" htmlFor="project-deploy-url">Deployment Link (after deploy)</label>
                <input id="project-deploy-url" className={inputCls} placeholder="https://client-site.com" value={deployUrl} onChange={(e) => setDeployUrl(e.target.value)} data-testid="project-deploy-url-input" />
              </div>
            </div>

            <DomainManager project={active} onSaved={setActive} />

            <MLModelPanel projectId={active.id} onTrained={() => openProject(active)} />

            <UMLPanel projectId={active.id} />

            <div className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800" data-testid="project-files-panel">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                  <div className="text-xs uppercase code-font text-indigo-700 dark:text-indigo-300 font-bold">Project Files (cloud storage)</div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Logos, images, documents — the client can download these. Copy Link gives a public URL to paste inside the site code (e.g. as an &lt;img&gt;).</p>
                </div>
                <label className={`${smallBtnCls} bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700/40 hover:bg-purple-100 dark:hover:bg-purple-500/20 cursor-pointer ${uploadingFile ? "opacity-60 pointer-events-none" : ""}`} data-testid="upload-file-button">
                  {uploadingFile ? "Uploading…" : "Upload File"}
                  <input type="file" className="hidden" onChange={uploadProjectFile} disabled={uploadingFile} data-testid="upload-file-input" />
                </label>
              </div>
              {files.length === 0 ? (
                <p className="text-xs text-slate-600 dark:text-slate-300 code-font" data-testid="files-empty">No files yet — upload the client's logo, images or documents.</p>
              ) : (
                <ul className="space-y-1.5" data-testid="files-list">
                  {files.map((f) => (
                    <li key={f.id} className="flex items-center gap-2 px-3 py-2 rounded bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800" data-testid={`file-item-${f.id}`}>
                      <span className="flex-1 min-w-0 truncate text-xs text-slate-700 dark:text-slate-200">{f.filename}</span>
                      <span className="text-[10px] code-font text-slate-500 dark:text-slate-400 shrink-0">{f.size >= 1048576 ? `${(f.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(f.size / 1024))} KB`}</span>
                      <button type="button" onClick={() => downloadProjectFile(f)} className="text-[10px] px-2 py-1 rounded border bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-700 hover:bg-slate-200 code-font transition-colors" data-testid={`file-download-${f.id}`}>Download</button>
                      <button type="button" onClick={() => copyFileLink(f)} className="text-[10px] px-2 py-1 rounded border bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-700/40 hover:bg-cyan-100 dark:hover:bg-cyan-500/20 code-font transition-colors" data-testid={`file-copy-link-${f.id}`}>Copy Link</button>
                      <button type="button" onClick={() => deleteProjectFile(f)} className="text-[10px] px-2 py-1 rounded border bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-700/40 hover:bg-rose-100 dark:hover:bg-rose-500/20 code-font transition-colors" data-testid={`file-delete-${f.id}`}>Delete</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800" data-testid="app-deploy-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase code-font text-indigo-700 dark:text-indigo-300 font-bold">App &amp; Deploy</div>
                {user?.role === "admin" && toolchain && (
                  <span
                    className={`text-[10px] code-font px-2 py-0.5 rounded-full border ${
                      toolchain.status === "ready" || toolchain.instant_apk
                        ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/40"
                        : "bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-700/40"
                    }`}
                    data-testid="toolchain-status-badge"
                    title={toolchain.status === "ready" ? "Full Android build tools installed" : `Full build tools: ${toolchain.status} — instant APK works without them`}
                  >
                    {toolchain.status === "ready" ? "Build tools: ready" : toolchain.instant_apk ? "Instant APK: ready" : toolchain.status === "installing" ? "Build tools: installing (~10 min)" : `Build tools: ${toolchain.status}`}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => startBuild("apk")} className={`${smallBtnCls} bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/40 hover:bg-emerald-100 dark:hover:bg-emerald-500/20`} data-testid="build-apk-button">Build APK</button>
                <button onClick={() => startBuild("aab")} className={`${smallBtnCls} bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-700/40 hover:bg-teal-100 dark:hover:bg-teal-500/20`} data-testid="build-aab-button">Build AAB</button>
                <button onClick={capacitorZip} className={`${smallBtnCls} bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-700/40 hover:bg-sky-100 dark:hover:bg-sky-500/20`} data-testid="capacitor-zip-button">Capacitor Zip</button>
                <label className={`${smallBtnCls} bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-700/40 hover:bg-orange-100 dark:hover:bg-orange-500/20 cursor-pointer ${uploadingApk ? "opacity-60 pointer-events-none" : ""}`} data-testid="upload-apk-button" title="Attach an APK/AAB built anywhere (e.g. in the preview) — no server build tools needed, downloads work from cloud storage">
                  {uploadingApk ? "Uploading…" : "Upload APK"}
                  <input type="file" accept=".apk,.aab" className="hidden" onChange={uploadAppBuild} disabled={uploadingApk} data-testid="upload-apk-input" />
                </label>
                <button onClick={requestDeploy} className={`${smallBtnCls} bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-700/40 hover:bg-rose-100 dark:hover:bg-rose-500/20`} data-testid="deploy-request-button">Request Deploy</button>
              </div>
              <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400 code-font">
                One design, three outputs: the published weblink (installable as an app via "Add to Home Screen") and the installable APK both update automatically every time you save or generate — no separate build press needed. AAB for Play Store can be attached with Upload APK when you need it.
              </p>
              {build && (
                <div className="mt-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-xs code-font" data-testid="build-status-card">
                  <span className="text-slate-500 dark:text-slate-400">{(build.build_type || "").toUpperCase()} build: </span>
                  <span
                    className={build.status === "done" ? "text-emerald-700 dark:text-emerald-300" : build.status === "failed" ? "text-rose-700 dark:text-rose-300" : "text-amber-800 dark:text-amber-300"}
                    data-testid="build-status"
                  >
                    {build.status === "building" ? "building… (this takes a few minutes)" : build.status}
                  </span>
                  {build.status === "done" && (
                    <button onClick={downloadBuild} className="ml-3 px-3 py-1 rounded bg-emerald-600 text-slate-900 dark:text-white border border-emerald-600 hover:bg-emerald-700 transition-colors" data-testid="download-build-button">
                      Download {(build.build_type || "").toUpperCase()}
                    </button>
                  )}
                  {build.status === "failed" && build.log_tail && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-rose-700 dark:text-rose-300" data-testid="build-log-toggle">Build log</summary>
                      <pre className="mt-1 whitespace-pre-wrap text-slate-500 dark:text-slate-400 max-h-40 overflow-auto">{build.log_tail}</pre>
                    </details>
                  )}
                </div>
              )}
            </div>

            {active?.project_type === "cyber" && active?.cyber_access !== "granted" && (
              <div className="rounded-xl border border-amber-300/60 dark:border-amber-400/30 bg-amber-50 dark:bg-amber-400/10 px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="cyber-access-banner">
                <div className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                  <span className="font-bold">Cybersecurity service project.</span>{" "}
                  {active?.cyber_access === "requested"
                    ? "Access requested — waiting for an admin's one-tap approval."
                    : "Admin approval is needed before generating — protects your credits and keeps security tools controlled."}
                </div>
                {active?.cyber_access !== "requested" && (
                  <button
                    onClick={() => requestCyberAccess(active)}
                    className="px-4 py-2 rounded-lg bg-amber-600 text-white text-xs font-bold uppercase tracking-wider hover:bg-amber-700 transition-colors"
                    data-testid="cyber-request-button"
                  >
                    Request Admin Access
                  </button>
                )}
              </div>
            )}
            <div className="accent-bar premium-card p-4 rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/[0.08]" data-testid="ai-generator">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="text-xs uppercase code-font neon-text font-bold tracking-wider">AI Draft Generator</div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => { setShowTemplates((v) => !v); setShowBriefBuilder(false); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs font-semibold code-font transition-colors ${
                      showTemplates
                        ? "bg-cyan-50 dark:bg-cyan-500/10 text-cyan-800 dark:text-cyan-200 border-cyan-300 dark:border-cyan-700/50"
                        : "bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-cyan-400"
                    }`}
                    data-testid="templates-toggle-button"
                    title="Ready starting points — free to pick, credits only when you press Generate"
                  >
                    <LayoutGrid className="w-3.5 h-3.5" /> Templates
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowBriefBuilder((v) => !v); setShowTemplates(false); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs font-semibold code-font transition-colors ${
                      showBriefBuilder
                        ? "bg-cyan-50 dark:bg-cyan-500/10 text-cyan-800 dark:text-cyan-200 border-cyan-300 dark:border-cyan-700/50"
                        : "bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-cyan-400"
                    }`}
                    data-testid="briefbuilder-toggle-button"
                    title="Answer simple questions (English/Telugu) — I build the brief for you, free"
                  >
                    <MessagesSquare className="w-3.5 h-3.5" /> Q&A Brief
                  </button>
                  <button
                    type="button"
                    onClick={() => setBriefLang((l) => (l === "en" ? "te" : "en"))}
                    className="px-2.5 py-1.5 rounded border text-[11px] font-bold code-font transition-colors bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-cyan-400"
                    data-testid="brief-lang-header-toggle"
                    title="Templates and questions language"
                  >
                    {briefLang === "en" ? "తె" : "EN"}
                  </button>
                </div>
              </div>
              {showTemplates && (
                <div className="mb-2">
                  <TemplateGallery lang={briefLang} onPick={(tpl) => applyBrief(tpl.brief)} />
                </div>
              )}
              {showBriefBuilder && (
                <div className="mb-2">
                  <BriefBuilder
                    lang={briefLang}
                    onToggleLang={() => setBriefLang((l) => (l === "en" ? "te" : "en"))}
                    onUseBrief={applyBrief}
                  />
                </div>
              )}
              <textarea
                className={`${inputCls} w-full min-h-[110px] resize-y`}
                placeholder="Describe the site in detail — sections, colors, language (Telugu/English), features… Example: 'Bakery landing page: hero, menu section with prices, order form, footer with phone and WhatsApp'"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                data-testid="ai-prompt-input"
              />
              <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
                <span className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={toggleRecording}
                    disabled={transcribing}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded border text-xs font-semibold code-font transition-colors disabled:opacity-60 ${
                      recording
                        ? "bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300 border-rose-300 dark:border-rose-700/50 animate-pulse"
                        : "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/40 hover:bg-emerald-100 dark:hover:bg-emerald-500/20"
                    }`}
                    data-testid="voice-mic-button"
                    title="Speak your brief in Telugu or English — it types itself"
                  >
                    {transcribing ? "Typing…" : recording ? (<><Square className="w-3 h-3" /> Stop</>) : (<><Mic className="w-3.5 h-3.5" /> Voice</>)}
                  </button>
                  <span className="text-[10px] code-font text-slate-500 dark:text-slate-400" data-testid="ai-credit-note">Typing/speaking is free — credits are used only when you press Generate</span>
                  <label
                    className="flex items-center gap-1.5 px-3 py-2 rounded border text-xs font-semibold code-font transition-colors cursor-pointer bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-700/40 hover:bg-teal-100 dark:hover:bg-teal-500/20"
                    data-testid="attach-images-button"
                    title="Attach photos from your device — they upload to the project and the AI uses them in the design (logo, hero, gallery)"
                  >
                    {attachingImages ? "Attaching…" : (<><ImagePlus className="w-3.5 h-3.5" /> Attach Images</>)}
                    <input type="file" accept="image/*" multiple className="hidden" onChange={attachImages} disabled={attachingImages || !active} data-testid="attach-images-input" />
                  </label>
                </span>
                <div className="flex items-center gap-2">
                  <select
                    className={inputCls}
                    value={genType}
                    onChange={(e) => setGenType(e.target.value)}
                    data-testid="gen-type-select"
                    title="What to create from your brief"
                  >
                    <option value="site">Website / App</option>
                    <option value="video">Video (DSS AI Video)</option>
                  </select>
                  {genType === "site" && (
                    <select
                      className={inputCls}
                      value={genModel}
                      onChange={(e) => setGenModel(e.target.value)}
                      data-testid="ai-model-select"
                      title="Cheaper model = fewer credits per generation"
                    >
                      <option value="gpt-5-nano">DSS AI Nano — cheapest</option>
                      <option value="claude-haiku-4-5-20251001">DSS AI Fast — Claude Haiku</option>
                      <option value="claude-sonnet-4-6">DSS AI Sonnet — Claude Sonnet</option>
                      <option value="gpt-5.4">DSS AI Ultra — best quality</option>
                      <option value="claude-sonnet-5-5">DSS AI Premium — Claude Sonnet 5.5</option>
                      <option value="claude-opus-4-8">DSS AI Opus — most powerful Claude</option>
                    </select>
                  )}
                  {genType === "site" && (
                    <button
                      onClick={() => generate("improve")}
                      disabled={generating || !prompt.trim() || !code}
                      className="px-4 py-2 rounded bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-700/40 hover:bg-amber-100 dark:hover:bg-amber-500/20 transition-colors text-xs font-semibold code-font disabled:opacity-60"
                      data-testid="ai-improve-button"
                      title="Apply your instruction to the current draft instead of starting over (e.g. 'add a contact section')"
                    >
                      {generating ? "Working…" : "Improve Draft"}
                    </button>
                  )}
                  <button
                    onClick={() => (genType === "video" ? generateVideo() : code ? setGenChoiceOpen(true) : generate("fresh"))}
                    disabled={generating || videoStatus === "running" || !prompt.trim() || (genType === "video" && videoEngine === "none") || (active?.project_type === "cyber" && active?.cyber_access !== "granted")}
                    className="px-4 py-2 rounded bg-cyan-700 text-slate-900 dark:text-white border border-cyan-700 hover:bg-cyan-800 transition-colors text-xs font-semibold code-font disabled:opacity-60"
                    data-testid="ai-generate-button"
                    title={active?.project_type === "cyber" && active?.cyber_access !== "granted" ? "Cybersecurity project — admin approval needed first" : genType === "video" ? (videoEngine === "none" ? "One-time setup first — see the guide below" : "Create a video from your brief") : code ? "A design exists — you'll be asked: improve it or replace it" : "Generate a new design"}
                  >
                    {genType === "video"
                      ? videoStatus === "running" ? "Creating video… (2–5 min)" : "Create Video"
                      : generating ? "Generating…" : "Generate Draft"}
                  </button>
                </div>
              </div>
              {genType === "video" && (
                <div className="mt-2 text-[11px]" data-testid="video-status-line">
                  {videoEngine === "none" && (
                    <div className="rounded-lg border border-amber-300/60 dark:border-amber-400/30 bg-amber-50 dark:bg-amber-400/10 px-3 py-2.5 text-amber-800 dark:text-amber-200" data-testid="video-setup-needed">
                      <div className="font-semibold">One-time setup for DSS AI Video</div>
                      <div className="mt-1 leading-relaxed">
                        Create a free key at aistudio.google.com, then paste it once here.
                        {onOpenTeam ? (
                          <> <button type="button" onClick={onOpenTeam} className="underline font-semibold hover:text-amber-900 dark:hover:text-amber-100" data-testid="video-setup-open-team">Open Team Access → API Keys</button> and save it under Google.</>
                        ) : (
                          <> Ask your admin to save it under Team Access → API Keys → Google.</>
                        )}
                      </div>
                    </div>
                  )}
                  {videoStatus === "running" && (
                    <span className="text-slate-500 dark:text-slate-400">Video rendering — you can keep working; it lands in the Files tab when done.</span>
                  )}
                  {videoStatus === "done" && (
                    <span className="font-bold text-emerald-600 dark:text-emerald-400">Video ready — it now plays as the site hero background, and it's in the Files tab to download.</span>
                  )}
                  {videoStatus === "error" && (
                    <div className="rounded-lg border border-amber-300/60 dark:border-amber-400/30 bg-amber-50 dark:bg-amber-400/10 px-3 py-2.5 text-amber-800 dark:text-amber-200" data-testid="video-error-notice">
                      <span className="font-semibold">Video didn't start.</span>{" "}
                      {/key|google|billing|permission/i.test(videoError)
                        ? "Check the Google key in Team Access → API Keys (it needs the Gemini API with video access and billing enabled)."
                        : videoError}
                    </div>
                  )}
                  {videoStatus === "idle" && videoEngine === "credits" && (
                    <span className="text-slate-400 dark:text-slate-500" data-testid="video-engine-credits">DSS AI Video: cartoon/animation prompts get TRUE motion animation; other prompts get cinematic painted scenes — all on a few DSS AI credits, no Google key needed.</span>
                  )}
                  {videoStatus === "idle" && videoEngine === "veo" && (
                    <span className="text-slate-400 dark:text-slate-500" data-testid="video-engine-veo">DSS AI Video (Veo-3) runs on your saved Google key — real footage, zero DSS AI credits used.</span>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center text-xs code-font text-slate-500 dark:text-slate-400">
                  <span className="flex items-center gap-3">
                    <span>index.html — editor</span>
                    <span className="relative">
                      <button
                        type="button"
                        onClick={() => setShowImagePicker((v) => !v)}
                        className="px-2 py-1 rounded border bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700/40 hover:bg-purple-100 dark:hover:bg-purple-500/20 transition-colors"
                        data-testid="insert-image-button"
                      >
                        Insert Image
                      </button>
                      {showImagePicker && (
                        <div className="absolute left-0 top-8 z-30 w-72 rounded-lg bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 shadow-xl p-2" data-testid="image-picker-panel">
                          {imageFiles.length === 0 ? (
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 p-2" data-testid="image-picker-empty">
                              No images uploaded yet — upload one in PROJECT FILES first.
                            </p>
                          ) : (
                            <ul className="max-h-48 overflow-y-auto space-y-1" data-testid="image-picker-list">
                              {imageFiles.map((f) => (
                                <li key={f.id}>
                                  <button
                                    type="button"
                                    onClick={() => insertImage(f)}
                                    className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 text-[11px] text-slate-700 dark:text-slate-200 truncate transition-colors"
                                    data-testid={`image-pick-${f.id}`}
                                  >
                                    {f.filename}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </span>
                  </span>
                  <span className="text-cyan-700 dark:text-cyan-300">{generating ? "STREAMING" : "READY"}</span>
                  <span
                    className={`text-[10px] font-bold ${
                      saveState === "saved"
                        ? "text-emerald-600 dark:text-emerald-400"
                        : saveState === "error"
                          ? "text-red-500"
                          : "text-amber-600 dark:text-amber-400"
                    }`}
                    data-testid="live-save-status"
                    title="Edits save automatically — the live site and the next APK download update too"
                  >
                    {saveState === "saving"
                      ? "Saving…"
                      : saveState === "unsaved"
                        ? "Unsaved changes…"
                        : saveState === "error"
                          ? "Save failed — press Save Build"
                          : "● LIVE — changes auto-saved"}
                  </span>
                </div>
                <textarea
                  ref={editorRef}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  spellCheck={false}
                  placeholder="<!-- Type or paste your HTML/CSS/JS here, or generate a draft above -->"
                  className="w-full h-72 lg:h-[480px] bg-slate-900 text-slate-100 code-font text-xs p-4 focus:outline-none resize-y"
                  data-testid="code-editor"
                />
              </div>
              <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-800 flex justify-between text-xs code-font text-slate-500 dark:text-slate-400">
                  <span>Live Output</span>
                  <span className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setAutoPreview((v) => !v)}
                      className={`px-2 py-0.5 rounded-full border text-[10px] font-bold transition-colors ${
                        autoPreview
                          ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/40"
                          : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-700"
                      }`}
                      data-testid="preview-mode-toggle"
                      title="Auto: preview refreshes as you type. Manual: press Run Preview to refresh."
                    >
                      {autoPreview ? "AUTO" : "MANUAL"}
                    </button>
                    <span className="text-emerald-700 dark:text-emerald-300">SANDBOXED</span>
                  </span>
                </div>
                {previewCode ? (
                  <iframe
                    sandbox="allow-scripts"
                    srcDoc={previewCode}
                    title="Site preview"
                    className="w-full h-72 lg:h-[480px] bg-white dark:bg-slate-900"
                    data-testid="workbench-preview-frame"
                  />
                ) : (
                  <div
                    className="w-full h-72 lg:h-[480px] bg-slate-50 dark:bg-slate-950 flex items-center justify-center text-center px-6"
                    data-testid="preview-empty-state"
                  >
                    <p className="text-xs text-slate-500 dark:text-slate-400 code-font">
                      No build yet — type code (the preview updates automatically as you type), or generate an AI draft above.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {genChoiceOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="gen-choice-modal">
            <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl p-5">
              <div className="text-sm font-bold text-slate-900 dark:text-white mb-1">This project already has a design</div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                Your instruction: keep the current design and change only what you asked — or start a brand-new design?
                (Either way, the current design stays recoverable in History.)
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => { setGenChoiceOpen(false); generate("improve", true); }}
                  className="w-full px-4 py-2.5 rounded-xl bg-cyan-700 text-white text-xs font-bold uppercase tracking-wider hover:bg-cyan-800 transition-colors"
                  data-testid="gen-choice-improve"
                >
                  Improve current design (recommended)
                </button>
                <button
                  onClick={() => { setGenChoiceOpen(false); generate("fresh"); }}
                  className="w-full px-4 py-2.5 rounded-xl border border-rose-300 dark:border-rose-700/50 bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 text-xs font-bold uppercase tracking-wider hover:bg-rose-100 dark:hover:bg-rose-500/20 transition-colors"
                  data-testid="gen-choice-replace"
                >
                  Replace with a new design
                </button>
                <button
                  onClick={() => setGenChoiceOpen(false)}
                  className="w-full px-4 py-2 rounded-xl text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                  data-testid="gen-choice-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
